import { useState } from "react";
import { toast } from "sonner";
import { Check, ArrowUpRight, Sparkles, Mail } from "lucide-react";
import { api } from "@/lib/api";
import { openCalendly } from "@/lib/calendly";

/* ============================================================
 * DCYB CERTIFIED PARTNER — pathway deep-dive
 * Sits between Audience (RU list) and Partnership (iMasons recent partner)
 * ============================================================ */

const TIERS = [
  {
    name: "Supporter",
    founding: "$1,500",
    standard: "$2,500",
    anchor: false,
    included: [
      "Certified Partner badge",
      "Listing on the DCYB partners page",
      "Recognition across the DCYB community",
    ],
  },
  {
    name: "Partner",
    founding: "$5,000",
    standard: "$7,500",
    anchor: false,
    included: [
      "Everything in Supporter",
      "Regular presence across DCYB social channels",
      "Inclusion in partner content",
      "Standing connection to the talent network",
    ],
  },
  {
    name: "Anchor",
    founding: "$12,500",
    standard: "$20,000",
    anchor: true,
    limited: true,
    included: [
      "Everything in Partner",
      "Featured placement",
      "Co-branded content and interviews",
      "Priority presence at DCYB events",
      "First access to the talent pipeline",
    ],
  },
];

export default function CertifiedPartner() {
  return (
    <section
      id="certified-partner"
      data-testid="certified-partner-section"
      className="border-b hairline bg-[#F4F5F7] relative overflow-hidden"
    >
      {/* Subtle scanline wash */}
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none opacity-[0.04]"
        style={{
          backgroundImage:
            "repeating-linear-gradient(0deg, #0F1117 0 1px, transparent 1px 4px)",
        }}
      />

      <div className="relative max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-32">
        {/* Eyebrow */}
        <div className="flex items-center justify-between mb-10 md:mb-14">
          <span
            className="font-mono-label text-[11px] tracking-[0.18em] text-[#0F1117]"
            data-testid="certified-partner-eyebrow"
          >
            / DCYB CERTIFIED PARTNER
          </span>
          <span className="font-mono-label text-[10px] text-[#71747B] hidden md:inline-flex items-center gap-2">
            <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#22C55E] animate-pulse" />
            FOUNDING COHORT OPEN
          </span>
        </div>

        {/* Positioning lede */}
        <div className="grid grid-cols-12 gap-y-10 gap-x-6 mb-16 md:mb-20">
          <div className="col-span-12 lg:col-span-5">
            <h2
              data-testid="certified-partner-headline"
              className="font-display text-3xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tight font-extrabold text-[#0F1117] text-balance"
            >
              Stand with the<br />
              <span className="text-chrome">next generation.</span>
            </h2>
          </div>
          <div className="col-span-12 lg:col-span-6 lg:col-start-7">
            <p
              data-testid="certified-partner-lede"
              className="text-[17px] sm:text-lg lg:text-[19px] text-[#3D4049] leading-[1.6] max-w-2xl"
            >
              The industry is at a handoff. The people who built data center
              infrastructure are starting to pass it to the generation that will
              run it for the next forty years. The companies that win the talent
              war are the ones building that relationship now, not when the gap
              is already costing them.
            </p>
            <p className="mt-4 text-[17px] sm:text-lg lg:text-[19px] text-[#3D4049] leading-[1.6] max-w-2xl">
              DCYB sits in the middle of that handoff. We bring together the
              next generation of operators, engineers, and builders, and we
              connect them to the companies serious about their future. A DCYB
              Certified Partner is a company that showed up early and stood with
              that generation while it mattered.
            </p>
          </div>
        </div>

        {/* Pathway entry — matches Audience row style */}
        <div className="border-y hairline">
          <div
            data-testid="certified-partner-pathway-row"
            className="grid grid-cols-12 items-start gap-x-0 sm:gap-x-6 py-8 md:py-10 px-1"
          >
            <div className="col-span-3 sm:col-span-1">
              <span className="font-mono-label text-[11px] text-[#0F1117]">
                RU.005
              </span>
            </div>
            <div className="col-span-9 sm:col-span-4">
              <h3 className="font-display text-2xl md:text-3xl font-bold text-[#0F1117] tracking-tight">
                DCYB Certified Partner
              </h3>
            </div>
            <div className="col-span-12 sm:col-span-7 mt-3 sm:mt-0">
              <p className="text-[15px] text-[#52555B] leading-relaxed italic">
                The companies building the future, standing with the people who
                will run it.
              </p>
            </div>
          </div>
        </div>

        {/* Body block */}
        <div className="mt-10 max-w-3xl">
          <p
            data-testid="certified-partner-body"
            className="text-[15px] md:text-base text-[#3D4049] leading-relaxed"
          >
            DCYB Certified Partner marks a company as an active partner of DCYB,
            publicly committed to engaging the next generation of operators,
            engineers, and builders. The badge certifies a real, ongoing
            relationship, displayed on the company&apos;s materials and on the
            DCYB platform alongside the community they support. The badge
            certifies the partnership is active. It does not represent an audit
            or ranking of the company.
          </p>
        </div>

        {/* Pricing cards */}
        <p aria-hidden="true" className="lg:hidden mt-10 -mb-6 font-mono-label text-[10px] !tracking-normal text-[#71747B]">Swipe to compare tiers →</p>
        <div
          data-testid="certified-partner-pricing"
          className="mt-10 md:mt-20 flex lg:grid lg:grid-cols-3 gap-4 md:gap-6 overflow-x-auto lg:overflow-visible snap-x snap-mandatory scroll-px-6 md:scroll-px-12 -mx-6 px-6 md:-mx-12 md:px-12 lg:mx-0 lg:px-0 pt-4 pb-3 lg:p-0 [&>*]:shrink-0 [&>*]:w-[84%] sm:[&>*]:w-[60%] md:[&>*]:w-[46%] lg:[&>*]:w-auto [&>*]:snap-start"
        >
          {TIERS.map((t) => (
            <TierCard key={t.name} tier={t} />
          ))}
        </div>

        {/* Founding partner note */}
        <p
          data-testid="certified-partner-founding-note"
          className="mt-10 md:mt-12 font-mono-label text-[10.5px] tracking-[0.14em] text-[#71747B] leading-relaxed max-w-3xl"
        >
          Founding rates are offered to the first cohort only, then close
          permanently. In return, founding partners agree to be named publicly
          as a DCYB founding partner, to provide one endorsement, and to let
          DCYB reference the relationship in outreach.
        </p>

        {/* Primary CTA — Dedicated form */}
        <CertifiedPartnerForm />

        {/* Secondary CTAs */}
        <div className="mt-8 md:mt-10 flex flex-wrap items-center gap-x-8 gap-y-3 pt-6 border-t hairline">
          <a
            href="/#book"
            onClick={openCalendly}
            data-testid="certified-partner-book-cta"
            className="group inline-flex items-center gap-2 font-mono-label text-[11px] tracking-[0.16em] text-[#52555B] hover:text-[#0F1117] transition-colors"
          >
            <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#22C55E]" />
            Or book a 30-min intro
            <ArrowUpRight size={14} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
          </a>
          <a
            href="mailto:Luke@DCYB.ai?subject=DCYB%20Certified%20Partner%20%E2%80%94%20Inquiry"
            data-testid="certified-partner-email-cta"
            className="group inline-flex items-center gap-2 font-mono-label text-[11px] tracking-[0.16em] text-[#52555B] hover:text-[#0F1117] transition-colors"
          >
            <Mail size={13} />
            Or email Luke directly
            <ArrowUpRight size={14} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
          </a>
        </div>
      </div>
    </section>
  );
}

/* ============================================================
 * Dedicated Certified Partner inquiry form
 * Posts to /api/certified-partner
 * ============================================================ */

const TIER_OPTIONS = [
  { value: "supporter", label: "Supporter" },
  { value: "partner", label: "Partner" },
  { value: "anchor", label: "Anchor — LIMITED" },
  { value: "undecided", label: "Not sure yet — let's talk" },
];

const initialForm = { name: "", email: "", company: "", tier: "", message: "" };

function CertifiedPartnerForm() {
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.company.trim() || !form.tier) {
      toast.error("Name, email, company, and tier are required.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/certified-partner", {
        name: form.name.trim(),
        email: form.email.trim(),
        company: form.company.trim(),
        tier: form.tier,
        message: form.message.trim(),
      });
      setDone(true);
      toast.success("Thanks. Luke will reach out shortly.");
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || "Something went wrong.";
      toast.error(typeof detail === "string" ? detail : "Submission failed.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div
        data-testid="certified-partner-form-done"
        className="mt-10 md:mt-12 chrome-surface relative p-7 md:p-9 border hairline fade-up"
      >
        <div className="font-mono-label text-[10px] tracking-[0.18em] text-[#0F1117] mb-3">
          / Inquiry received
        </div>
        <h3 className="font-display text-2xl md:text-3xl font-extrabold tracking-tight text-[#0F1117] mb-3">
          {`Thanks${form.name ? `, ${form.name.split(" ")[0]}` : ""}.`}
        </h3>
        <p className="text-[15px] text-[#3D4049] leading-relaxed max-w-xl">
          Your interest in the DCYB Certified Partner program is logged. Luke
          will reach out shortly to set up a 30-minute intro on Google Meet and
          walk you through the founding-cohort terms.
        </p>
        <button
          type="button"
          onClick={() => {
            setForm(initialForm);
            setDone(false);
          }}
          data-testid="certified-partner-form-reset"
          className="mt-5 font-mono-label text-[11px] text-[#5F6168] hover:text-[#0F1117] transition-colors"
        >
          ← Submit another
        </button>
      </div>
    );
  }

  return (
    <form
      onSubmit={submit}
      data-testid="certified-partner-form"
      className="mt-10 md:mt-14 chrome-surface relative p-6 md:p-8 border hairline"
    >
      <div className="flex items-center justify-between mb-6">
        <span className="font-mono-label text-[10.5px] tracking-[0.18em] text-[#0F1117]">
          / Step into the founding cohort
        </span>
        <span className="font-mono-label text-[9.5px] text-[#71747B] hidden sm:inline-flex items-center gap-1.5">
          <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#22C55E] animate-pulse" />
          REPLIES FROM LUKE
        </span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
        <Field label="Name *" htmlFor="cp-name">
          <input
            id="cp-name"
            data-testid="cp-name-input"
            type="text"
            autoComplete="name"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            className={inputCls}
            placeholder="Jane Doe"
          />
        </Field>
        <Field label="Work email *" htmlFor="cp-email">
          <input
            id="cp-email"
            data-testid="cp-email-input"
            type="email"
            autoComplete="email"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            className={inputCls}
            placeholder="you@company.com"
          />
        </Field>
        <Field label="Company *" htmlFor="cp-company">
          <input
            id="cp-company"
            data-testid="cp-company-input"
            type="text"
            autoComplete="organization"
            value={form.company}
            onChange={(e) => setForm({ ...form, company: e.target.value })}
            className={inputCls}
            placeholder="Your company name"
          />
        </Field>
        <Field label="Tier of interest *" htmlFor="cp-tier">
          <select
            id="cp-tier"
            data-testid="cp-tier-select"
            value={form.tier}
            onChange={(e) => setForm({ ...form, tier: e.target.value })}
            className={`${inputCls} appearance-none cursor-pointer pr-10`}
            style={{
              backgroundImage:
                "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath fill='%2371747B' d='M6 8L0 0h12z'/%3E%3C/svg%3E\")",
              backgroundRepeat: "no-repeat",
              backgroundPosition: "right 16px center",
            }}
          >
            <option value="" disabled>
              Select a tier
            </option>
            {TIER_OPTIONS.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </select>
        </Field>
      </div>

      <div className="mt-4 md:mt-5">
        <Field label="What are you hoping to get from the partnership?" htmlFor="cp-msg">
          <textarea
            id="cp-msg"
            data-testid="cp-message-input"
            rows={3}
            value={form.message}
            onChange={(e) => setForm({ ...form, message: e.target.value })}
            className={`${inputCls} resize-none`}
            placeholder="Optional. Goals, timing, anything we should know going into the intro."
          />
        </Field>
      </div>

      <button
        type="submit"
        disabled={submitting}
        data-testid="cp-submit-btn"
        className="chrome-btn-dark chrome-btn-dark-glow mt-6 md:mt-7 w-full md:w-auto inline-flex items-center justify-center gap-2 font-mono-label text-[12px] tracking-[0.14em] px-7 py-4 disabled:opacity-60"
      >
        <span className="live-dot" aria-hidden />
        {submitting ? "Submitting…" : "Become a Certified Partner"}
        {!submitting && <ArrowUpRight size={16} />}
      </button>
    </form>
  );
}

function Field({ label, htmlFor, children }) {
  return (
    <label htmlFor={htmlFor} className="block">
      <span className="font-mono-label text-[10.5px] tracking-[0.14em] text-[#5F6168] mb-2 block uppercase">
        {label}
      </span>
      {children}
    </label>
  );
}

const inputCls =
  "chrome-surface w-full rounded-none px-4 py-3 text-[14.5px] text-[#0F1117] focus:outline-none focus:border-[#0F1117] focus:ring-1 focus:ring-[#0F1117] transition-colors placeholder:text-[#9498A0]";

function TierCard({ tier }) {
  const isAnchor = tier.anchor;
  return (
    <a
      href="/#book"
      onClick={openCalendly}
      data-testid={`tier-card-${tier.name.toLowerCase()}`}
      className={`group relative chrome-surface bg-white p-6 md:p-7 flex flex-col h-full cursor-pointer transition-transform duration-200 hover:-translate-y-1 hover:shadow-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0F1117] ${
        isAnchor ? "anchor-glow" : "border hairline"
      }`}
    >
      {tier.limited && (
        <span
          data-testid={`tier-card-${tier.name.toLowerCase()}-limited-tag`}
          className="absolute -top-3 right-5 inline-flex items-center gap-1.5 px-2.5 py-1 bg-[#0F1117] text-white font-mono-label text-[9.5px] tracking-[0.18em] rounded-full shadow-md"
        >
          <Sparkles size={10} className="text-[#E5C36A]" />
          LIMITED SPOTS
        </span>
      )}

      {/* Tier header */}
      <div className="flex items-center justify-between mb-6">
        <h4 className="font-display text-2xl md:text-3xl font-extrabold tracking-tight text-[#0F1117]">
          {tier.name}
        </h4>
        <span className="font-mono-label text-[9.5px] text-[#71747B]">
          / TIER
        </span>
      </div>

      {/* Founding cohort rate — prominent (prices behind CTA) */}
      <div className="mb-6 pb-6 border-b hairline">
        <div
          className={`font-display font-extrabold tracking-tight leading-[1.05] ${
            isAnchor ? "text-[26px] md:text-[30px] text-chrome" : "text-[24px] md:text-[28px] text-[#0F1117]"
          }`}
        >
          Founding cohort rate
        </div>
        <div className="font-mono-label text-[9.5px] tracking-[0.14em] text-[#71747B] mt-3 leading-relaxed">
          {isAnchor
            ? "FIRST COHORT · LIMITED SPOTS"
            : "FIRST COHORT"}
        </div>
      </div>

      {/* Included list */}
      <ul className="space-y-3 mb-6">
        {tier.included.map((item) => (
          <li key={item} className="flex items-start gap-3">
            <span
              className={`shrink-0 mt-0.5 inline-flex items-center justify-center w-4 h-4 ${
                isAnchor ? "bg-[#0F1117] text-white" : "bg-[#0F1117]/95 text-white"
              }`}
            >
              <Check size={10} strokeWidth={3} />
            </span>
            <span className="text-[14.5px] text-[#3D4049] leading-relaxed">
              {item}
            </span>
          </li>
        ))}
      </ul>

      {/* Request pricing — whole card is the link */}
      <div className="mt-auto pt-4 border-t hairline">
        <span
          data-testid={`tier-card-${tier.name.toLowerCase()}-request-pricing`}
          className="inline-flex items-center gap-2 font-mono-label text-[11px] tracking-[0.14em] text-[#0F1117] group-hover:text-[#22C55E] transition-colors"
        >
          <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#22C55E]" />
          Request pricing
          <ArrowUpRight
            size={13}
            className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
          />
        </span>
      </div>
    </a>
  );
}
